import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import {
  notificationDurationShort,
  notificationDurationMiddle,
  notificationDurationLong,
} from "../../lib/utils";

const defaultFormInitialValues = {
  Id: 0,
  Name: "",
  Description: "",
  Claims: [],
  // CanCreateDatasets: false, CanEditDatasets: false, CanDeleteDatasets: false, CanViewDatasets: false,
  // CanCreateExpressions: false, CanEditExpressions: false, CanDeleteExpressions: false, CanViewExpressions: false,
  // CanCreateDataSources: false, CanEditDataSources: false, CanDeleteDataSources: false, CanViewDataSources: false,
  // CanCreateAlerts: false, CanEditAlerts: false, CanDeleteAlerts: false, CanViewAlerts: false,
  // CanCreateUsers: false, CanEditUsers: false, CanDeleteUsers: false, CanViewUsers: false,
  // CanCreateReports: false, CanEditReports: false, CanDeleteReports: false,
  // CanCreateData: false, CanEditData: false, CanDeleteData: false, CanViewData: false,
  // NotifyDataProcessing: false,
  UsersIds: [],
  Users: [],
  ReportsIds: [],
  Reports: [],
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],
  lookups: {
    users: [],
    reports: [],
    claims: [],
  },
  entities: [],
  toolsVisible: true,
  panes: [
    {
      // size: "30%",
      size: "20%",
      // min: "17%",
      // max: "22%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "60%",
      max: "60%",
      collapsible: false,
    },
    {
      //min: "700px",
      size: "20%",
      min: "20%",
      max: "20%",
      collapsible: false,
    },
  ],
};

const securityGroupsPageSlice = createSlice({
  name: "securityGroupsPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    updateSecurityGroupClaims: (state, action) => {
      return {
        ...state,
        formValues: { ...state.formValues, Claims: action.payload },
      };
    },

    setFormClientErrors: (state, action) => ({
      ...state,
      formClientErrors: { ...action.payload },
    }),

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => {
      //payload: {Users: ,Groups: ,Reports:  }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const claims = action.payload.Claims || [];
      const updatedClaims = claims.map((group) => {
        if (group.Feature === "notMapped") {
          return {
            ...group,
            Claims: group.Claims.map((c) =>
              c.ClaimKey === "ViewConsumption"
                ? { ...c, Claim: "ViewConsumption" }
                : c
            ),
          };
        }
        return group;
      });

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        mode: 0,
        formKey: new Date().toString(),
        formValues: {
          ...defaultFormInitialValues,
          Claims: updatedClaims,
        },
        formInitialValues: {
          ...defaultFormInitialValues,
          Claims: updatedClaims,
        },
        formClientErrors: {},
        formServerErrors: [],
        lookups: {
          users: action.payload.Users,
          reports: action.payload.Reports,
          claims: action.payload.Claims,
        },
        entities: action.payload.Groups,
      };
    },

    entityLoaded: (state, action) => {
      //action.payload: the fetched entity + ClientId is "" if null in Db + Username=Password=PasswordConfirmed=""
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const claims = action.payload.Claims || [];
      const updatedClaims = claims.map((group) => {
        if (group.Feature === "notMapped") {
          return {
            ...group,
            Claims: group.Claims.map((c) =>
              c.ClaimKey === "ViewConsumption"
                ? { ...c, Claim: "ViewConsumption" }
                : c
            ),
          };
        }
        return group;
      });

      const usersIds = action.payload.UsersIds;
      const reportsIds = action.payload.ReportsIds;

      return {
        ...state,
        processing: false,
        processingText: "",
        mode: 2,
        formKey: new Date().toString(),
        formInitialValues: {
          ...action.payload,
          Users: state.lookups.users.map((e) => ({
            Id: e.Id,
            Name: e.Name,
            Checked: usersIds.find((g) => g == e.Id) ? true : false,
          })),
          Reports: state.lookups.reports.map((e) => ({
            Id: e.Id,
            Title: e.Title,
            Checked: reportsIds.find((g) => g == e.Id) ? true : false,
          })),
          Claims: updatedClaims,
        },
        formValues: {
          ...action.payload,
          Users: state.lookups.users.map((e) => ({
            Id: e.Id,
            Name: e.Name,
            Checked: usersIds.find((g) => g == e.Id) ? true : false,
          })),
          Reports: state.lookups.reports.map((e) => ({
            Id: e.Id,
            Title: e.Title,
            Checked: reportsIds.find((g) => g == e.Id) ? true : false,
          })),
          Claims: updatedClaims,
        },
        formClientErrors: {},
        formServerErrors: [],
      };
    },

    createEntity: (state, action) => {
      const pageState = current(state);

      const updatedClaims = state.lookups.claims.map((group) => {
        if (group.Feature === "notMapped") {
          return {
            ...group,
            Claims: group.Claims.map((c) =>
              c.ClaimKey === "ViewConsumption"
                ? { ...c, Claim: "ViewConsumption" }
                : c
            ),
          };
        }
        return group;
      });
      return {
        ...state,
        mode: 1,
        formKey: new Date().toString(),
        formInitialValues: {
          ...defaultFormInitialValues,
          Users: state.lookups.users.map((e) => ({
            Id: e.Id,
            Name: e.Name,
            Checked: false,
          })),
          Reports: state.lookups.reports.map((e) => ({
            Id: e.Id,
            Title: e.Title,
            Checked: false,
          })),
          Claims: updatedClaims,
        },
        formValues: {
          ...defaultFormInitialValues,
          Users: state.lookups.users.map((e) => ({
            Id: e.Id,
            Name: e.Name,
            Checked: false,
          })),
          Reports: state.lookups.reports.map((e) => ({
            Id: e.Id,
            Title: e.Title,
            Checked: false,
          })),
          Claims: updatedClaims,
        },
        formClientErrors: {},
        formServerErrors: [],
      };
    },

    entityInserted: (state, action) => {
      //payload: id
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const id = action.payload;

      const insertedEntity = { ...pageState.formValues, Id: id };

      return {
        ...state,
        mode: 2,
        formKey: new Date().toString(),
        formValues: { ...insertedEntity },
        formInitialValues: { ...insertedEntity },
        formClientErrors: {},
        formServerErrors: [],
        entities: [...state.entities, insertedEntity],
      };
    },

    entityUpdated: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const entity = pageState.formValues;

      //reflect on entities.name

      return {
        ...state,
        formKey: new Date().toString(),
        formInitialValues: { ...state.formValues },
        formClientErrors: {},
        formServerErrors: [],
        entities: state.entities.map((e) => {
          if (e.Id == entity.Id) return { ...e, Name: entity.Name };

          return e;
        }),
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      let entityId = action.payload;

      //remove from entities
      let entities = pageState.entities.filter((e) => e.Id != entityId);

      //if it's the current entity => goto no mode
      let currentMode = {};
      if (pageState.mode == 2 && pageState.formValues.Id == entityId)
        currentMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: { ...defaultFormInitialValues },
          formValues: { ...defaultFormInitialValues },
          formClientErrors: {},
          formServerErrors: [],
        };

      return { ...state, entities: entities, ...currentMode };
    },

    entityDeleted: (state, action) => {
      //payload: id of the deleted entity
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const deletedEntityId = action.payload;
      let isCurrentEntity = pageState.formValues.Id == deletedEntityId;

      let newMode = {};

      if (isCurrentEntity)
        newMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: { ...defaultFormInitialValues },
          formValues: { ...defaultFormInitialValues },
          formClientErrors: {},
          formServerErrors: [],
        };

      return {
        ...state,
        entities: state.entities.filter((e) => e.Id != deletedEntityId),
        ...newMode,
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default securityGroupsPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "securitygroup/getsecurityGroupspagedata",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().securityGroupsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingGroup") })
  );

  try {
    var response = await api({
      url: "securitygroup/getsecuritygroupbyid",
      method: "post",
      data: {
        id: entityId,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().securityGroupsPage.pageDisposed) return;

    let data = response.data.Data;

    //transform data

    dispatch(entityLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const saveEntity = (t) => async (dispatch, getState) => {
  const pageState = getState().securityGroupsPage;

  let entity = pageState.formValues;
  let entityId = entity.Id;
  let url =
    pageState.mode == 1
      ? "securitygroup/createsecuritygroup"
      : "securitygroup/updatesecuritygroup";

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.savingGroup") })
  );

  try {
    var response = await api({
      url: url,
      method: "post",
      data: {
        ...pageState.formValues,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().securityGroupsPage.pageDisposed) return;

    let data = response.data.Data;

    if (pageState.mode == 1) dispatch(entityInserted(data.Id));
    else dispatch(entityUpdated());

    let successMessage =
      pageState.mode == 1
        ? t("slices.savedGroup")
        : t("slices.updatedGroup");

    dispatch(
      addNotification({
        text: successMessage,
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      })
    );

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.deletingGroup") })
  );

  try {
    var response = await api({
      url: "/securitygroup/removesecuritygroup",
      method: "post",
      data: { id: id },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().securityGroupsPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      addNotification({
        text: t("slices.deletedGroup"),
        type: "success",
        closable: true,
        duration: 3,
        position: "topRight",
      })
    );

    dispatch(entityDeleted(id));

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  pageLoaded,
  setPanes,
  createEntity,
  setFormValue,
  setFormClientErrors,
  setFormServerErrors,
  entityLoaded,
  removeEntityFromUI,
  entityInserted,
  entityUpdated,
  entityDeleted,
  disposePage,
  updateSecurityGroupClaims,
} = securityGroupsPageSlice.actions;
